import { Fragment, useContext, useEffect, useState } from 'react';

import { ToastService } from '@iamsoftware/react-hooks';

import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { classNames } from 'primereact/utils';

import { LayoutContext } from 'src/layout/context/layoutcontext';
import { AuthService } from 'src/service/AuthService';
import { AppStorage } from 'src/service/UserStorage';

export default function Login() {

  const { layoutConfig } = useContext(LayoutContext);
  const [formType, setFormType] = useState<string>('Login');

  const containerClassName = classNames('auth-page surface-ground flex align-items-center justify-content-center min-h-screen min-w-screen overflow-hidden relative', { 'p-input-filled': layoutConfig.inputStyle === 'filled' });

  return (
    <div className={containerClassName}>
      <style>{`
        @keyframes auth-fade-up {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .auth-page {
          padding: 7rem 1.5rem 4.5rem;
          background:
            radial-gradient(circle at 12% 8%, color-mix(in srgb, var(--primary-color) 11%, transparent) 0, transparent 30%),
            radial-gradient(circle at 88% 88%, rgba(33, 150, 243, 0.08) 0, transparent 32%),
            var(--surface-ground);
        }

        .auth-ambient {
          position: absolute;
          width: 32rem;
          height: 32rem;
          border-radius: 50%;
          border: 5rem solid color-mix(in srgb, var(--primary-color) 5%, transparent);
          pointer-events: none;
        }

        .auth-ambient-left {
          top: -19rem;
          left: -17rem;
        }

        .auth-ambient-right {
          right: -18rem;
          bottom: -20rem;
        }

        .auth-logo {
          display: flex;
          justify-content: center;
          margin-bottom: 2.25rem;
        }

        .auth-logo img {
          display: block;
          width: min(13.5rem, 82%);
          height: auto;
        }

        .auth-shell {
          width: 100%;
          max-width: 26.25rem;
          animation: auth-fade-up 420ms ease-out both;
        }

        .auth-card {
          padding: 2.5rem 2.25rem 2rem;
          border: 1px solid var(--surface-border);
          border-radius: 1.5rem;
          box-shadow: 0 1.5rem 4.5rem color-mix(in srgb, var(--primary-color) 13%, transparent);
        }

        .auth-form {
          gap: 1rem;
        }

        .auth-field {
          display: flex;
          flex-direction: column;
          gap: 0.45rem;
        }

        .auth-field label {
          color: var(--text-color);
          font-size: 0.8125rem;
          font-weight: 600;
        }

        .auth-field .p-inputtext,
        .auth-field .p-password-input {
          width: 100%;
          min-height: 3rem;
          border-radius: 0.75rem;
        }

        .auth-field .p-password,
        .auth-field .p-password .p-icon-field {
          display: block;
          width: 100%;
        }

        .auth-field .p-password-input {
          width: 100% !important;
        }

        .auth-submit.p-button {
          min-height: 3rem;
          margin-top: 0.25rem;
          border-radius: 0.75rem;
          font-weight: 600;
        }

        .auth-link {
          padding: 0;
          border: 0;
          background: transparent;
          color: var(--primary-color);
          font: inherit;
          font-size: 0.8125rem;
          cursor: pointer;
        }

        .auth-link:hover {
          text-decoration: underline;
        }

        @media (max-width: 575px) {
          .auth-page {
            align-items: flex-start !important;
            padding: 6.5rem 1rem 4rem;
          }

          .auth-card {
            padding: 2rem 1.25rem 1.5rem;
            border-radius: 1.25rem;
          }

        }
      `}</style>

      <div className="auth-ambient auth-ambient-left" />
      <div className="auth-ambient auth-ambient-right" />

      <div className="auth-shell z-1">
        <div className="auth-card surface-card">
          <div className="auth-logo">
            <img src="/iam/iam-econtract.png" alt="IAM eContract" />
          </div>

          {formType === 'Login' && <LoginForm setFormType={setFormType} />}
          {formType === 'ResetPassword' && <ResetPasswordForm setFormType={setFormType} />}

          {/*
            Tạm thời tắt luồng đăng nhập SSO.
            Khi bật lại, dùng AuthService.getSsoLoginUrl() rồi redirect sang data.redirectUrl như phiên bản SSO trước đó.
          */}
        </div>
      </div>
    </div>
  );
}

const LoginForm = ({ setFormType }) => {

  const [submitting, setSubmitting] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [partyTaxId, setPartyTaxId] = useState('');

  useEffect(() => {
    getToken();
    if (AppStorage.get('partyTaxId')) {
      setPartyTaxId(AppStorage.get('partyTaxId'));
    }
  }, []);

  const getToken = () => {
    AuthService.getToken().then(({ moquiSessionToken }) => {
      AppStorage.set('moquiSessionToken', moquiSessionToken);
    });
  };

  const handleSubmit = event => {
    event.preventDefault();

    if (submitting) return;

    const loginUsername = partyTaxId ? `${username}_${partyTaxId}` : username;

    setSubmitting(true);
    AuthService.login(loginUsername, password).then(data => {
      if (data.isSuccess === 'Y') {
        AppStorage.remove('accessToken');
        AppStorage.remove('refreshToken');
        AppStorage.remove('accessTokenExpireAt');
        AppStorage.remove('refreshTokenExpireAt');
        AppStorage.set('moquiSessionToken', data.moquiSessionToken);
        AppStorage.set('partyTaxId', partyTaxId);
        window.location.hash = '/';
      } else {
        const errors = data?.errors?.length ? data.errors : ['Đăng nhập không thành công'];
        errors.forEach(error => ToastService.error(error));
        getToken();
      }
    }).catch(err => {
      const errorMsg = err?.response?.data?.errors || err?.response?.data?.error || err?.message || 'Có lỗi xảy ra khi đăng nhập';
      ToastService.error(errorMsg);
      getToken();
    }).finally(() => {
      setSubmitting(false);
    });
  };

  return (
    <Fragment>
      <form onSubmit={handleSubmit} className="auth-form flex flex-column mx-auto">
        <div className="auth-field">
          <label htmlFor="partyTaxId">Mã số thuế</label>
          <InputText id="partyTaxId" placeholder="Nhập mã số thuế" value={partyTaxId} onChange={e => setPartyTaxId(e.target.value)} />
        </div>

        <div className="auth-field">
          <label htmlFor="username">Tài khoản</label>
          <InputText id="username" placeholder="Nhập tài khoản" value={username} onChange={e => setUsername(e.target.value)} required />
        </div>

        <div className="auth-field">
          <label htmlFor="password">Mật khẩu</label>
          <Password id="password" placeholder="Nhập mật khẩu" value={password} onChange={e => setPassword(e.target.value)} toggleMask feedback={false} className="w-full" inputClassName="w-full" required />
        </div>

        <Button label="Đăng nhập" icon="pi pi-arrow-right" iconPos="right" className="auth-submit w-full" loading={submitting} />
      </form>
      <div className="flex justify-content-end mt-3">
        <button type="button" className="auth-link" onClick={() => setFormType('ResetPassword')}>Quên mật khẩu?</button>
      </div>
    </Fragment>
  );
};

const ResetPasswordForm = ({ setFormType }) => {
  const [submitting, setSubmitting] = useState(false);
  const [username, setUsername] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [partyTaxId, setPartyTaxId] = useState('');

  useEffect(() => {
    if (AppStorage.get('partyTaxId')) {
      setPartyTaxId(AppStorage.get('partyTaxId'));
    }
  }, []);

  const handleSubmit = event => {
    event.preventDefault();

    if (submitting) return;

    const resetUsername = partyTaxId ? `${username}_${partyTaxId}` : username;

    setSubmitting(true);
    AuthService.resetPassword(resetUsername, emailAddress).then(() => {
      ToastService.success('Đặt lại mật khẩu thành công. Quý khách vui lòng kiểm tra Hộp thư đến email.');
      setFormType('Login');
    }).catch(err => {
      const errorMsg = err?.response?.data?.errors || err?.response?.data?.error || err?.message || 'Có lỗi xảy ra khi lấy lại mật khẩu';
      ToastService.error(errorMsg);
    }).finally(() => {
      setSubmitting(false);
    });
  };

  return (
    <Fragment>
      <form onSubmit={handleSubmit} className="auth-form flex flex-column mx-auto">
        <div className="auth-field">
          <label htmlFor="partyTaxId">Mã số thuế</label>
          <InputText id="partyTaxId" placeholder="Nhập mã số thuế" value={partyTaxId} onChange={e => setPartyTaxId(e.target.value)} />
        </div>

        <div className="auth-field">
          <label htmlFor="username">Tài khoản</label>
          <InputText id="username" placeholder="Nhập tài khoản" value={username} onChange={e => setUsername(e.target.value)} required />
        </div>

        <div className="auth-field">
          <label htmlFor="emailAddress">Email</label>
          <InputText id="emailAddress" type="email" placeholder="Nhập email" value={emailAddress} onChange={e => setEmailAddress(e.target.value)} required />
        </div>

        <Button label="Lấy lại mật khẩu" icon="pi pi-arrow-right" iconPos="right" className="auth-submit w-full" loading={submitting} />
      </form>
      <div className="flex mt-3">
        <button type="button" className="auth-link" onClick={() => setFormType('Login')}><i className="pi pi-arrow-left mr-2" aria-hidden="true" />Quay lại đăng nhập</button>
      </div>
    </Fragment>
  );
};
